import React from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { usePlatformScout } from "context/PlatformScoutContext";

function DownloadItem({ item }) {
    const { isDownloading, progress, profileData, error, isComplete } = item;

    let icon = <CircularProgress size={16} color="white" />;
    let statusText = `${progress}%`;
    let color = "white";

    if (isComplete) {
        icon = <CheckCircleIcon fontSize="small" sx={{ color: "#4caf50" }} />;
        statusText = "Done";
        color = "#4caf50";
    } else if (error) {
        icon = <ErrorIcon fontSize="small" color="error" />;
        statusText = "Failed";
        color = "error.main";
    }

    return (
        <MDBox display="flex" alignItems="center" justifyContent="space-between" width="100%" mb={0.5}>
            <MDBox display="flex" alignItems="center" gap={1} style={{ overflow: "hidden" }}>
                {/* We can add platform icon here if we pass it or derive it, but name is enough for now */}
                <MDTypography variant="caption" color="white" fontWeight="medium" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "150px" }}>
                    {profileData?.name}
                </MDTypography>
            </MDBox>

            <MDBox display="flex" alignItems="center" gap={1}>
                <MDTypography variant="caption" color="white" fontWeight="bold">
                    {statusText}
                </MDTypography>
                {icon}
            </MDBox>
        </MDBox>
    );
}

export default function GlobalDownloadToast() {
    const { downloadState, closeToast } = usePlatformScout();
    const { items, showToast } = downloadState;

    const activeItems = Object.values(items);
    // Optional: Reverse to show newest on top? Or just map.
    // Let's filter out "very old" completed ones if we implemented cleanup, but for now show all.

    if (!showToast || activeItems.length === 0) return null;

    const hasActive = activeItems.some((i) => i.isDownloading);

    return (
        <Snackbar
            open={showToast}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            onClose={(event, reason) => {
                if (reason === "clickaway" && hasActive) return;
                closeToast();
            }}
        >
            <Alert
                onClose={closeToast}
                severity="info"
                icon={false}
                sx={{
                    width: "100%",
                    minWidth: "320px",
                    maxWidth: "400px",
                    backgroundColor: "#344767", // Dark background for better list contrast
                    color: "white",
                    "& .MuiAlert-message": { width: "100%" }
                }}
                variant="filled"
                elevation={6}
            >
                <MDBox display="flex" flexDirection="column" width="100%">
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <MDTypography variant="button" color="white" fontWeight="bold">
                            Profile Downloads ({activeItems.length})
                        </MDTypography>
                    </MDBox>

                    <Divider sx={{ my: 0.5, backgroundColor: "rgba(255,255,255,0.2)" }} />

                    <MDBox display="flex" flexDirection="column" gap={0.5} mt={0.5} style={{ maxHeight: "200px", overflowY: "auto", overflowX: "hidden" }}>
                        {activeItems.map((item) => (
                            <DownloadItem key={`${item.profileData?.platform}_${item.profileData?.id}`} item={item} />
                        ))}
                    </MDBox>
                </MDBox>
            </Alert>
        </Snackbar>
    );
}
